/**
 * O traçado automático de Taylor e de Casagrande — e os tempos de norma.
 *
 * «Trace automaticamente as retas para definir o Cv por Taylor e
 *  Casagrande. Caso o usuário queira, ele pode ajustar como é hoje. Além
 *  disso, deixe os pontos dos tempos de norma destacados no gráfico, como
 *  no gráfico da Suporte» — Humberto, 14/09/2026.
 *
 * ── O que isto é, e o que não é ──────────────────────────────────────
 *
 * É um PONTO DE PARTIDA para as alças. Nada aqui grava nada: as retas
 * propostas caem nas mesmas alças que a pessoa arrasta, e o que vai para
 * o banco continua sendo H₀, H₉₀/H₁₀₀ e t₉₀/t₅₀ de onde ELA parou. A conta
 * do cv continua no banco (`coeficiente_adensamento`, 0024).
 *
 * A NT 162 já tinha mostrado por que um ajuste ingênuo falha: a curva
 * densa da prensa traz o transiente de aplicação de carga nos primeiros
 * segundos, e todo ajuste por mínimos quadrados sobre a curva inteira se
 * agarra nele. Por isso as duas rotinas abaixo (a) descartam o que vem
 * antes de 6 s — o primeiro tempo de leitura da norma —, (b) trabalham
 * por FRAÇÃO do recalque do estágio, e não por tempo absoluto, e (c)
 * devolvem `null` quando não há trecho para ajustar, em vez de inventar
 * uma reta. A tela, nesse caso, cai no chute antigo e diz que é chute.
 *
 * Tudo aqui é função pura sobre (t em s, H em mm) — testável sem tela.
 */

export interface LeituraTH { t: number; h: number }
export interface P { x: number; y: number }

/**
 * Os tempos de leitura da NBR 16853:2020 (7.3.2), em segundos: 6 s, 15 s,
 * 30 s, 1, 2, 4, 8, 15, 30 min, 1, 2, 4, 8 e 24 h. É neles que o técnico
 * lê no relógio comparador, e é neles que o gráfico da Suporte marca o
 * ponto cheio — a prensa lê a cada segundo, e sem a marca a curva é uma
 * fita contínua onde ninguém acha «a leitura de 8 min».
 */
export const TEMPOS_NORMA_S: readonly number[] = [
  6, 15, 30, 60, 120, 240, 480, 900, 1800, 3600, 7200, 14400, 28800, 86400,
];

/** Tolerância de 3 % (ou 1 s, o que for maior): o arquivo da prensa raramente cai no segundo exato. */
export function ehTempoDeNorma(t: number): number | null {
  for (const tn of TEMPOS_NORMA_S) {
    if (Math.abs(t - tn) <= Math.max(1, tn * 0.03)) return tn;
  }
  return null;
}

/** Rótulo curto de um tempo de norma: «15 s», «8 min», «24 h». */
export function rotuloTempo(t: number): string {
  if (t < 60) return `${Math.round(t)} s`;
  if (t < 3600) return `${Math.round(t / 60)} min`;
  return `${Math.round(t / 3600)} h`;
}

/** Mínimos quadrados: y = m·x + b. `null` com menos de 2 pontos distintos. */
export function retaMQ(pontos: P[]): { m: number; b: number } | null {
  const n = pontos.length;
  if (n < 2) return null;
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (const p of pontos) { sx += p.x; sy += p.y; sxx += p.x * p.x; sxy += p.x * p.y; }
  const den = n * sxx - sx * sx;
  if (Math.abs(den) < 1e-12) return null;
  const m = (n * sxy - sx * sy) / den;
  return { m, b: (sy - m * sx) / n };
}

/** H interpolado linearmente em t (a curva vem ordenada por t). */
export function hEm(curva: LeituraTH[], t: number): number | null {
  if (!curva.length) return null;
  if (t <= curva[0].t) return t === curva[0].t ? curva[0].h : null;
  for (let i = 1; i < curva.length; i++) {
    const a = curva[i - 1], b = curva[i];
    if (t <= b.t) {
      if (b.t === a.t) return b.h;
      return a.h + (b.h - a.h) * (t - a.t) / (b.t - a.t);
    }
  }
  return null;
}

/** t em que a curva cruza a altura `h` (interpolado em log t), ou `null`. */
export function tEmAltura(curva: LeituraTH[], h: number): number | null {
  for (let i = 1; i < curva.length; i++) {
    const a = curva[i - 1], b = curva[i];
    if ((a.h - h) * (b.h - h) <= 0 && a.h !== b.h && a.t > 0 && b.t > 0) {
      const f = (a.h - h) / (a.h - b.h);
      const la = Math.log10(a.t), lb = Math.log10(b.t);
      return 10 ** (la + f * (lb - la));
    }
  }
  return null;
}

const limpar = (curva: LeituraTH[]) => curva
  .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.h) && p.t > 0)
  .sort((a, b) => a.t - b.t);

/**
 * Taylor — a reta inicial no plano √t.
 *
 * O trecho reto do adensamento primário fica, na teoria de Terzaghi, até
 * cerca de 60 % do recalque. Ajusta-se por mínimos quadrados o que está
 * entre 10 % e 60 % do recalque do estágio, a partir de 6 s. Devolve as
 * duas alças da reta A (a que passa por d₀), no plano (√t, H).
 */
export function ajusteTaylor(bruta: LeituraTH[]): [P, P] | null {
  const curva = limpar(bruta);
  if (curva.length < 5) return null;
  const alto = curva[0].h, baixo = curva[curva.length - 1].h;
  const total = alto - baixo;
  if (!(total > 0)) return null;
  const fracao = (h: number) => (alto - h) / total;
  let trecho = curva.filter((p) => p.t >= 6 && fracao(p.h) >= 0.10 && fracao(p.h) <= 0.60);
  if (trecho.length < 3) trecho = curva.filter((p) => fracao(p.h) >= 0.05 && fracao(p.h) <= 0.70);
  if (trecho.length < 3) return null;
  const reta = retaMQ(trecho.map((p) => ({ x: Math.sqrt(p.t), y: p.h })));
  if (!reta || !(reta.m < 0)) return null;   // a altura tem de CAIR com √t
  const x1 = Math.sqrt(trecho[0].t);
  const x2 = Math.sqrt(trecho[trecho.length - 1].t);
  return [
    { x: x1, y: reta.m * x1 + reta.b },
    { x: x2, y: reta.m * x2 + reta.b },
  ];
}

export interface AjusteCasagrande {
  /** t₁ e t₂ = 4·t₁ usados em 7.4.2.3 para achar H₀. */
  t1: number; t2: number;
  h0: number;
  /** Alças da tangente ao ponto de inflexão, no plano (t, H) — o x é t, e a tela põe em log. */
  tangente: [P, P];
  /** Alças da reta do adensamento secundário. */
  secundaria: [P, P];
}

/**
 * H₀ pelo método da parábola (7.4.2.3): a diferença de altura entre t₁ e
 * t₂ = 4 t₁, marcada acima da leitura de t₁. t₁ vem dos tempos de norma;
 * prefere-se o maior t₁ em que t₂ ainda está antes de 50 % do recalque —
 * quanto mais longe do transiente, mais estável.
 */
export function h0Casagrande(bruta: LeituraTH[], t1Pedido?: number):
  { t1: number; t2: number; h0: number } | null {
  const curva = limpar(bruta);
  if (curva.length < 4) return null;
  const alto = curva[0].h, baixo = curva[curva.length - 1].h;
  const total = alto - baixo;
  if (!(total > 0)) return null;
  const tenta = (t1: number) => {
    const t2 = 4 * t1;
    const h1 = hEm(curva, t1), h2 = hEm(curva, t2);
    if (h1 == null || h2 == null) return null;
    if (!(h1 > h2)) return null;
    return { t1, t2, h0: h1 + (h1 - h2) };
  };
  if (t1Pedido) return tenta(t1Pedido);
  const candidatos = [60, 30, 15, 6];
  for (const t1 of candidatos) {
    const r = tenta(t1);
    if (!r) continue;
    const h2 = hEm(curva, r.t2)!;
    if ((alto - h2) / total <= 0.5) return r;
  }
  return tenta(15) ?? tenta(6);
}

/**
 * A inflexão da curva H × log t — medida numa JANELA DE LOG t.
 *
 * ── O defeito que isto conserta (Humberto, 14/09/2026) ───────────────
 *
 * «O cálculo automático de Cv de Casagrande não está funcionando, os
 *  pontos não estão sendo posicionados sobre a curva.» Não estavam mesmo,
 * e o motivo é aritmético: a primeira versão procurava a inflexão na
 * janela de TRÊS LEITURAS SEGUIDAS com a maior queda por década. Num
 * ensaio da Testesolo a prensa grava ~750 leituras por estágio, e três
 * leituras seguidas no fim da curva distam **0,00001 década** — qualquer
 * ruído de meio micrômetro dividido por isso vira uma inclinação
 * astronômica. O ajuste encontrava a «inflexão» no ruído (estágio de
 * 12,5 kPa: t = 856 s, 1,25 mm/década onde a curva está plana) e as alças
 * da tangente, calculadas a meia década do centro, caíam **0,6 mm** acima
 * e abaixo da curva — fora do gráfico.
 *
 * Com a curva do PDF da Suporte o defeito não aparecia: lá as leituras
 * são as catorze da norma, três pontos seguidos cobrem meia década, e a
 * conta se comporta. Foi por isso que a onda 187 a conferiu como boa.
 *
 * A largura da janela passa a ser fixa em log t (e não em número de
 * leituras): o denominador deixa de ser um número quase nulo e a
 * inclinação vira a da CURVA, não a do ruído. Devolve o centro da janela
 * de maior queda, com a reta ajustada nela.
 */
const LARGURA_JANELA_DECADA = 0.4;

export function inflexaoEmLogT(uteis: LeituraTH[]):
  { t: number; h: number; m: number } | null {
  if (uteis.length < 5) return null;
  const L = uteis.map((p) => ({ x: Math.log10(p.t), y: p.h }));
  const x0 = L[0].x, xf = L[L.length - 1].x;
  const meia = LARGURA_JANELA_DECADA / 2;
  if (xf - x0 < LARGURA_JANELA_DECADA) return null;
  let melhor: { x: number; m: number; b: number } | null = null;
  for (const centro of L) {
    const xc = centro.x;
    /* A janela tem de estar CHEIA dos dois lados: meia janela em cima da
       borda mede a borda, não a curva. */
    if (xc - meia < x0 - 1e-9 || xc + meia > xf + 1e-9) continue;
    const janela = L.filter((p) => p.x >= xc - meia && p.x <= xc + meia);
    if (janela.length < 4) continue;
    const r = retaMQ(janela);
    if (!r || !(r.m < 0)) continue;
    if (!melhor || r.m < melhor.m) melhor = { x: xc, m: r.m, b: r.b };
  }
  if (!melhor) return null;
  return {
    t: 10 ** melhor.x,
    /* A altura do CENTRO vem da reta ajustada, e não da leitura: é a
       curva suavizada, e é o que põe a alça em cima da curva mesmo
       quando a leitura daquele segundo pulou. */
    h: melhor.m * melhor.x + melhor.b,
    m: melhor.m,
  };
}

/**
 * Casagrande — H₀, a tangente no ponto de inflexão e a reta secundária,
 * no plano log t.
 *
 * A inflexão sai de `inflexaoEmLogT` (janela de 0,4 década). A reta do
 * adensamento secundário é ajustada na ÚLTIMA DÉCADA de leituras — e não
 * «o que vem depois de 10 × t_inflexão», que num estágio de inflexão
 * tardia caía nos três últimos pontos, a segundos um do outro, com a
 * mesma divisão por quase-zero. Onde as duas se cruzam está H₁₀₀ — a
 * tela faz essa conta a partir das alças, porque as alças são o que a
 * pessoa vai mover.
 */
export function ajusteCasagrande(bruta: LeituraTH[], t1Pedido?: number): AjusteCasagrande | null {
  const curva = limpar(bruta);
  if (curva.length < 6) return null;
  const h0 = h0Casagrande(curva, t1Pedido);
  if (!h0) return null;

  const uteis = curva.filter((p) => p.t >= 6);
  if (uteis.length < 5) return null;

  const inf = inflexaoEmLogT(uteis);
  if (!inf) return null;
  const xi = Math.log10(inf.t);

  /*
   * A cauda: a última década de leituras, depois de 4 × t_inflexão (o
   * secundário não começa antes de o primário acabar). Se sobrar pouco,
   * afrouxa para a última década; em último caso, o último terço em
   * log t — nunca «os três últimos», que numa curva densa são três
   * segundos seguidos.
   */
  const tf = uteis[uteis.length - 1].t;
  const depois = (t0: number) => uteis.filter((p) => p.t >= t0);
  let cauda = depois(Math.max(tf / 10, 4 * inf.t));
  if (cauda.length < 4) cauda = depois(tf / 10);
  if (cauda.length < 4) cauda = depois(10 ** (Math.log10(tf) - (Math.log10(tf) - Math.log10(uteis[0].t)) / 3));
  if (cauda.length < 3) return null;
  const rs = retaMQ(cauda.map((p) => ({ x: Math.log10(p.t), y: p.h })));
  if (!rs) return null;

  /* As alças da tangente a ±0,3 década da inflexão, sobre a reta dela;
     as da secundária, nas pontas da cauda. */
  const naTangente = (x: number) => ({ x: 10 ** x, y: inf.h + inf.m * (x - xi) });
  const xs1 = Math.log10(cauda[0].t), xs2 = Math.log10(cauda[cauda.length - 1].t);
  return {
    ...h0,
    tangente: [naTangente(xi - 0.3), naTangente(xi + 0.3)],
    secundaria: [
      { x: 10 ** xs1, y: rs.m * xs1 + rs.b },
      { x: 10 ** xs2, y: rs.m * xs2 + rs.b },
    ],
  };
}

/** Cruzamento de duas retas dadas por dois pontos cada, no plano (log t, H). */
export function cruzamentoLog(a: [P, P], b: [P, P]): { t: number; h: number } | null {
  const la = a.map((p) => ({ x: Math.log10(p.x), y: p.y }));
  const lb = b.map((p) => ({ x: Math.log10(p.x), y: p.y }));
  const ma = (la[1].y - la[0].y) / (la[1].x - la[0].x);
  const mb = (lb[1].y - lb[0].y) / (lb[1].x - lb[0].x);
  if (!Number.isFinite(ma) || !Number.isFinite(mb) || Math.abs(ma - mb) < 1e-12) return null;
  const ba = la[0].y - ma * la[0].x;
  const bb = lb[0].y - mb * lb[0].x;
  const x = (bb - ba) / (ma - mb);
  return { t: 10 ** x, h: ma * x + ba };
}
